import { XmlParseError, amount, child, children, joinNonEmpty, text } from "./dom";
import { XmlDocumentType, XmlInvoice, XmlLine, XmlParty, XmlPayment, XmlVatRecap } from "./types";

const NAMESPACE_PREFIX = "http://isdoc.cz/namespace/";

const DOCUMENT_TYPES: Record<string, XmlDocumentType> = {
    "1": "invoice",
    "2": "credit_note",
    "3": "debit_note",
    "4": "advance_invoice",
    "5": "advance_tax_document",
    "6": "advance_credit_note",
    "7": "simplified_invoice",
};

type AmountReader = (element: Element | null, name: string) => number | null;

function amountReader(foreignCurrency: boolean): AmountReader {
    return (element, name) => amount(element, foreignCurrency ? `${name}Curr` : name);
}

function vatNumber(party: Element | null): string | null {
    const schemes = children(party, "PartyTaxScheme");
    const vat = schemes.find((s) => text(s, "TaxScheme") === "VAT") ?? schemes[0] ?? null;

    return text(vat, "CompanyID");
}

function parseParty(element: Element | null): XmlParty {
    const party = child(element, "Party");
    const address = child(party, "PostalAddress");

    return {
        name: text(party, "PartyName", "Name"),
        companyNumber: text(party, "PartyIdentification", "ID"),
        vatNumber: vatNumber(party),
        street: joinNonEmpty([text(address, "StreetName"), text(address, "BuildingNumber")]),
        city: text(address, "CityName"),
        postalZone: text(address, "PostalZone"),
        country: text(address, "Country", "IdentificationCode"),
    };
}

function parsePayment(payment: Element): XmlPayment {
    const details = child(payment, "Details");
    const accountId = text(details, "ID");

    return {
        meansCode: text(payment, "PaymentMeansCode"),
        bankAccount: accountId ? joinNonEmpty([accountId, text(details, "BankCode")], "/") : null,
        iban: text(details, "IBAN"),
        bic: text(details, "BIC"),
    };
}

function unitPrice(
    line: Element,
    foreignCurrency: boolean,
    quantity: number | null,
): number | null {
    if (!foreignCurrency) return amount(line, "UnitPrice");

    const total = amount(line, "LineExtensionAmountCurr");

    return total !== null && quantity ? total / quantity : null;
}

function parseLine(line: Element, read: AmountReader, foreignCurrency: boolean): XmlLine {
    const quantity = amount(line, "InvoicedQuantity");

    return {
        id: text(line, "ID"),
        description: text(line, "Item", "Description"),
        quantity,
        unit: child(line, "InvoicedQuantity")?.getAttribute("unitCode") || null,
        unitPrice: unitPrice(line, foreignCurrency, quantity),
        vatRate: amount(line, "ClassifiedTaxCategory", "Percent"),
        totalExclVat: read(line, "LineExtensionAmount"),
        totalInclVat: read(line, "LineExtensionAmountTaxInclusive"),
    };
}

function parseVatRecap(subtotal: Element, read: AmountReader): XmlVatRecap {
    return {
        rate: amount(subtotal, "TaxCategory", "Percent"),
        base: read(subtotal, "TaxableAmount"),
        vat: read(subtotal, "TaxAmount"),
        total: read(subtotal, "TaxInclusiveAmount"),
    };
}

function rootElement(doc: Document): Element {
    const root = doc.documentElement;

    if (root.localName !== "Invoice" || !root.namespaceURI?.startsWith(NAMESPACE_PREFIX)) {
        throw new XmlParseError(
            `Not an ISDOC document: ${root.namespaceURI ?? ""} ${root.localName}`,
        );
    }
    if (!child(root, "LegalMonetaryTotal")) throw new XmlParseError("Missing LegalMonetaryTotal");

    return root;
}

export default function parseIsdoc(doc: Document): XmlInvoice {
    const root = rootElement(doc);
    const foreignCurrencyCode = text(root, "ForeignCurrencyCode");
    const read = amountReader(!!foreignCurrencyCode);
    const payments = children(child(root, "PaymentMeans"), "Payment");
    const firstDetails = payments.map((p) => child(p, "Details")).find(Boolean) ?? null;
    const taxTotal = child(root, "TaxTotal");
    const monetaryTotal = child(root, "LegalMonetaryTotal");

    return {
        documentType: DOCUMENT_TYPES[text(root, "DocumentType") ?? ""] ?? "invoice",
        number: text(root, "ID"),
        variableSymbol: text(firstDetails, "VariableSymbol"),
        issueDate: text(root, "IssueDate"),
        taxPointDate: text(root, "TaxPointDate"),
        dueDate: text(firstDetails, "PaymentDueDate"),
        currency: foreignCurrencyCode ?? text(root, "LocalCurrencyCode"),
        supplier: parseParty(child(root, "AccountingSupplierParty")),
        customer: parseParty(child(root, "AccountingCustomerParty")),
        payments: payments.map(parsePayment),
        lines: children(child(root, "InvoiceLines"), "InvoiceLine").map((l) =>
            parseLine(l, read, !!foreignCurrencyCode),
        ),
        vatRecap: children(taxTotal, "TaxSubTotal").map((s) => parseVatRecap(s, read)),
        totals: {
            lineTotal: null,
            allowances: null,
            charges: null,
            exclVat: read(monetaryTotal, "TaxExclusiveAmount"),
            vat: read(taxTotal, "TaxAmount"),
            inclVat: read(monetaryTotal, "TaxInclusiveAmount"),
            rounding: read(monetaryTotal, "PayableRoundingAmount"),
            paidDeposits: read(monetaryTotal, "PaidDepositsAmount"),
            payable: read(monetaryTotal, "PayableAmount"),
        },
    };
}
